import React from 'react';
import { Box, Typography, Paper, Grid2, LinearProgress, Tooltip, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SAVED_HOME_LIMITS, MONTHLY_WALKTHROUGH_LIMITS } from '../../shared/constants';

const UsageStatistics: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  if (!user) return null;
  
  // Determine limits based on subscription tier
  let homesLimit, walkthroughLimit;
  
  if (user.subscription_tier === 'pro') {
    homesLimit = SAVED_HOME_LIMITS.PRO;
    walkthroughLimit = MONTHLY_WALKTHROUGH_LIMITS.PRO;
  } else if (user.subscription_tier === 'agent') {
    homesLimit = SAVED_HOME_LIMITS.AGENT;
    walkthroughLimit = MONTHLY_WALKTHROUGH_LIMITS.AGENT;
  } else {
    homesLimit = SAVED_HOME_LIMITS.FREE;
    walkthroughLimit = MONTHLY_WALKTHROUGH_LIMITS.FREE;
  }
  
  // Calculate percentages for progress bars
  const savedHomesPercentage = Math.min(100, (user.active_homes_count / homesLimit) * 100);
  const walkthroughsPercentage = Math.min(100, (user.homes_this_month_count / walkthroughLimit) * 100);
  
  const handleUpgradeClick = () => {
    navigate('/pricing');
  };
  
  // Determine if user has premium access (pro or agent tier)
  const hasPremiumAccess = user.subscription_tier === 'pro' || user.subscription_tier === 'agent';
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        mb: 3, 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
        Account Usage
        <Tooltip title="Your subscription limits and current usage">
          <InfoIcon fontSize="small" sx={{ ml: 1, fontSize: '16px', color: 'text.secondary' }} />
        </Tooltip>
      </Typography>
      
      <Grid2 container spacing={2}>
        <Grid2 size={{xs: 12, md: 6}}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2">Saved Homes</Typography>
              <Typography variant="body2" fontWeight="500">
                {user.active_homes_count} / {homesLimit}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={savedHomesPercentage} 
              color={savedHomesPercentage > 85 ? "warning" : "primary"}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        </Grid2>
        
        <Grid2 size={{xs: 12, md: 6}}>
          <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2">Monthly Walkthroughs</Typography>
              <Typography variant="body2" fontWeight="500">
                {user.homes_this_month_count} / {walkthroughLimit}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={walkthroughsPercentage} 
              color={walkthroughsPercentage > 85 ? "warning" : "primary"}
              sx={{ height: 6, borderRadius: 1 }}
            />
          </Box>
        </Grid2>
      </Grid2>
      
      {hasPremiumAccess ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
          {user.subscription_tier === 'agent' 
            ? "You have agent access with increased limits"
            : "You're on the Pro plan with increased limits"
          }
        </Typography>
      ) : (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 1, mr: 1 }}>
            Need more capacity? Upgrade to Pro
          </Typography>
          <Button
            size="small"
            color="secondary"
            variant="outlined"
            onClick={handleUpgradeClick}
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              minWidth: '90px',        // Set a minimum width
              px: 1.5,                 // Add horizontal padding
              py: 0.5,                 // Add vertical padding
              textTransform: 'none',   // Keep original casing
              fontWeight: 'medium',
              whiteSpace: 'nowrap'     // Prevent text wrapping
            }}
          >
            Upgrade
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default UsageStatistics;